'use client';

import { Suspense, useContext, useEffect, useId, useRef, useState } from 'react';

import { makeVar } from '@apollo/client';
import { FocusTrap } from 'focus-trap-react';
import { useInView } from 'motion/react';

import { type Masked, unmask } from '~/contentful/graphql';
import { IsNavigationCondensedContext } from '~/system/providers/IsNavigationCondensed/IsNavigationCondensed.context';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Breakpoint } from '~/ui/styles/grid';
import { useScrollLock } from '~/v1/hooks/useScrollLock';

import styles from './navigation.module.scss';
import { PrimaryNavigation } from './primary';
import { type Navigation_QueryFragment } from './query';
import { SecondaryNavigation } from './secondary';

export type NavigationItem = 'ideas' | 'pages' | 'search' | 'resources';
export type AnimationDuration = 'default' | 'instant';

// most keyboard animations are faster (probably around 400ms), but to be on the safe side we set it to 1s
const KEYBOARD_ANIMATION_DURATION = 1000;

export const navigationOpenedVar = makeVar(false);

export interface NavigationProps {
  navigation: Masked<typeof Navigation_QueryFragment>;
  desktopPrimaryContent?: React.ReactNode;
  showDesktopPrimaryContent?: boolean;
}

export function Navigation({
  navigation: data,
  desktopPrimaryContent,
  showDesktopPrimaryContent,
}: NavigationProps) {
  const [activeNavigation, setActiveNavigation] = useState<NavigationItem>();
  const [primaryIsExpanded, setPrimaryIsExpanded] = useState(false);
  const breakpoint = useBreakpoint();
  const secondaryNavId = useId();
  const ref = useRef(null);

  const isSecondaryExpanded = !!activeNavigation;
  const isSmallOrMedium = breakpoint === Breakpoint.MOBILE || breakpoint === Breakpoint.TABLET;

  if (activeNavigation && !primaryIsExpanded) {
    setPrimaryIsExpanded(true);
  }

  useEffect(() => {
    navigationOpenedVar(isSecondaryExpanded);
  }, [isSecondaryExpanded]);

  const { setIsNavigationCondensed, isNavigationCondensed } = useContext(
    IsNavigationCondensedContext,
  );
  const hasUserScrolled = !useInView(ref, { amount: 1 });

  useEffect(() => {
    setIsNavigationCondensed(hasUserScrolled);
  }, [setIsNavigationCondensed, hasUserScrolled]);

  const shouldLockScroll = isSecondaryExpanded || (isSmallOrMedium && primaryIsExpanded);

  const { ref: scrollRef } = useScrollLock<HTMLDivElement>(
    shouldLockScroll,
    isSecondaryExpanded.toString(),
  );

  const navigationData = unmask<typeof Navigation_QueryFragment>(data);

  const setNavigationPadding = () => {
    if (scrollRef?.current && scrollRef.current.id === secondaryNavId && visualViewport) {
      scrollRef.current.style.paddingBottom = `${window.innerHeight - visualViewport.height}px`;
    }
  };

  const handleSearchFocusChange = () => {
    setTimeout(setNavigationPadding, KEYBOARD_ANIMATION_DURATION);
  };

  return (
    <FocusTrap
      active={isSecondaryExpanded && !isSmallOrMedium}
      focusTrapOptions={{ initialFocus: false }}
    >
      <div className={styles.navigationWrapper} ref={ref}>
        <PrimaryNavigation
          setActiveNavigation={setActiveNavigation}
          activeNavigation={activeNavigation}
          primaryIsExpanded={primaryIsExpanded}
          setPrimaryIsExpanded={setPrimaryIsExpanded}
          isCondensed={isSmallOrMedium && isNavigationCondensed}
          scrollLockRef={!isSecondaryExpanded ? scrollRef : undefined}
          onSearchClick={handleSearchFocusChange}
          desktopContent={desktopPrimaryContent}
          showDesktopContent={showDesktopPrimaryContent}
        />
        {/* SecondaryNavigation uses useSearchParams which disables static rendering up to the nearest suspense boundary */}
        <Suspense>
          <SecondaryNavigation
            id={secondaryNavId}
            data={navigationData}
            setActivePrimaryNavigation={setActiveNavigation}
            activePrimaryNavigation={activeNavigation}
            setPrimaryIsExpanded={setPrimaryIsExpanded}
            scrollLockRef={isSecondaryExpanded ? scrollRef : undefined}
            onSearchFocusChange={handleSearchFocusChange}
          />
        </Suspense>
      </div>
    </FocusTrap>
  );
}
